import React, { useState, useEffect } from 'react';
import { Search, Filter, Trophy, Medal, Award, TrendingUp, User, Clock, Users } from 'lucide-react';
import LeaderboardCard from './LeaderboardCard';
import LeaderboardFilters from './LeaderboardFilters';
import leaderboardService from '../../services/leaderboardService';

const QuestionnaireLeaderboard = ({ questionnaire, filters, onFilterChange }) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userPosition, setUserPosition] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const userId = JSON.parse(localStorage.getItem("usuarioConCedula") || "{}").id;

  useEffect(() => {
    if (questionnaire) {
      loadLeaderboardData();
      loadUserPosition();
      loadAnalytics();
    }
  }, [questionnaire, filters, currentPage]);

  const loadLeaderboardData = async () => {
    if (!questionnaire) return;

    try {
      setLoading(true);
      setError(null);
      
      // Use the best score leaderboard endpoint since there's no questionnaire-specific endpoint
      const data = await leaderboardService.getBestScoreLeaderboardData({
        top: 50
      });
      
      setLeaderboardData(data || []);
      setTotalPages(Math.ceil((data?.length || 0) / 20));
    } catch (error) {
      console.error('Error loading leaderboard data:', error);
      setError('Error al cargar los datos del leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const loadUserPosition = async () => {
    if (!questionnaire || !userId) return;

    try {
      // Use the user ranking endpoint for best score
      const ranking = await leaderboardService.getUserRanking(userId, 'BestScore');
      setUserPosition(ranking);
    } catch (error) {
      console.error('Error loading user position:', error);
    }
  };

  const loadAnalytics = async () => {
    if (!questionnaire) return;

    try {
      // Use global statistics for analytics
      const analyticsData = await leaderboardService.getGlobalStatistics();
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const getMedalIcon = (position) => {
    switch (position) {
      case 1:
        return <Trophy className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        return null;
    }
  };

  const getPositionColor = (position) => {
    switch (position) {
      case 1:
        return 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white';
      case 2:
        return 'bg-gradient-to-r from-gray-300 to-gray-400 text-white';
      case 3:
        return 'bg-gradient-to-r from-amber-500 to-amber-600 text-white';
      default:
        return 'bg-white text-gray-900';
    }
  };

  const formatScore = (score) => {
    return `${score.toFixed(1)}%`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!questionnaire) {
    return (
      <div className="p-8 text-center">
        <div className="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
          <Trophy className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Selecciona un cuestionario</h3>
        <p className="text-gray-600">Elige un cuestionario para ver su tabla de clasificación</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {questionnaire.title}
            </h2>
            <p className="text-gray-600">
              Clasificación de usuarios por mejor puntuación
            </p>
          </div>
          {userPosition && (
            <div className="bg-green-aqua/10 border border-green-aqua/20 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-aqua" />
                <span className="text-sm font-medium text-green-aqua">
                  Tu posición: #{userPosition.position || 'N/A'}
                </span>
              </div>
              <div className="text-xs text-gray-600 mt-1">
                Puntuación: {formatScore(userPosition.score || userPosition.bestScore || 0)}
              </div>
            </div>
          )}
        </div>

        {/* Analytics Summary */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">Participantes</span>
              </div>
              <div className="text-2xl font-bold text-blue-900">{analytics.totalUsers || 0}</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-600">Puntuación Promedio</span>
              </div>
              <div className="text-2xl font-bold text-green-900">{formatScore(analytics.averageAccuracy || 0)}</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-600">Mejor Puntuación</span>
              </div>
              <div className="text-2xl font-bold text-yellow-900">{formatScore(analytics.highestAccuracy || 0)}</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-600">Cuestionarios</span>
              </div>
              <div className="text-2xl font-bold text-purple-900">{analytics.totalQuestionnaires || 0}</div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <LeaderboardFilters
        filters={filters}
        onFilterChange={onFilterChange}
        questionnaireId={questionnaire.id}
      />

      {/* Leaderboard Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full border-4 border-green-aqua/20 border-t-green-aqua animate-spin"></div>
            <p className="text-gray-600">Cargando clasificación...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 text-2xl">!</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadLeaderboardData}
              className="px-4 py-2 bg-green-aqua text-white rounded-lg hover:bg-green-aqua/90 transition-colors"
            >
              Reintentar
            </button>
          </div>
        ) : leaderboardData.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <Trophy className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos</h3>
            <p className="text-gray-600">Aún no hay participantes en este cuestionario</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Posición
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mejor Puntuación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Intentos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Último Intento
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leaderboardData.map((entry, index) => {
                  const position = (currentPage - 1) * 20 + index + 1;
                  const score = entry.bestScore || entry.score || entry.averageScore || 0;
                  const attempts = entry.totalAttempts || entry.attempts || 0;
                  const lastAttempt = entry.lastActivity || entry.lastAttempt;
                  
                  return (
                    <tr
                      key={entry.userId}
                      className={`hover:bg-gray-50 transition-colors ${
                        entry.userId === parseInt(userId) ? 'bg-green-aqua/5' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${getPositionColor(position)}`}>
                            {position}
                          </div>
                          {getMedalIcon(position)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <img
                              className="h-10 w-10 rounded-full"
                              src={entry.userAvatar || "/img/user.png"}
                              alt={entry.userName}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {entry.userName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {entry.userEmail}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-bold text-gray-900">
                            {formatScore(score)}
                          </div>
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-aqua h-2 rounded-full transition-all duration-300"
                              style={{ width: `${score}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {attempts}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lastAttempt ? formatDate(lastAttempt) : 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionnaireLeaderboard; 